import { useEffect, useRef, useState } from 'react';
import { toast } from 'react-hot-toast';
import { Paperclip, Plus, Trash2 } from 'lucide-react';
import { FollowupPanel } from '@/features/activity/components/TimelinePanel';
import { useAccountsCustomers } from '@/features/accounts/hooks/useParties';
import { useProducts } from '@/features/catalog/hooks/useCatalogApi';
import { useMasterItems } from '@/features/master-data/hooks/useMasters';
import { DocumentActions } from '@/shared/components/documents/DocumentActions';
import { Badge } from '@/shared/components/ui/Badge';
import { Button } from '@/shared/components/ui/Button';
import { Card, CardBody, CardHeader } from '@/shared/components/ui/Card';
import { Drawer } from '@/shared/components/ui/Drawer';
import { EmptyState } from '@/shared/components/ui/EmptyState';
import { Input } from '@/shared/components/ui/Input';
import { Modal } from '@/shared/components/ui/Modal';
import { SearchableSelect } from '@/shared/components/ui/SearchableSelect';
import { Select } from '@/shared/components/ui/Select';
import { Spinner } from '@/shared/components/ui/Spinner';
import { useUser } from '@/shared/hooks/useAuth';
import { useUserOptions } from '@/shared/hooks/useUserOptions';
import {
  complaintAttachmentUrl,
  useComplaintEligibleInvoices,
  useComplaintReportSummary,
  useServiceComplaint,
  useServiceComplaints,
  useServiceDeskMutations,
} from '../api';
import type { ComplaintFilters, ComplaintStage, ServiceComplaint, SourceChannel, TicketPriority } from '../types';
import { formatDateTime, humanize, personName, priorityTone } from '../viewModel';

const STAGE_LABELS: Record<ComplaintStage, string> = {
  registered: 'Registered',
  acknowledged: 'Acknowledged',
  sample_collection: 'Sample Collection',
  investigation: 'Investigation',
  corrective_action: 'Corrective Action',
  resolution: 'Resolution',
  verification: 'Verification',
  closure: 'Closure',
};

// Mirrors ATTACHMENT_MIME_KINDS / MAX_ATTACHMENTS_PER_REQUEST in
// src/api-service-desk/attachments.js — the server is authoritative, this only
// steers the file picker and gives an immediate error instead of a round trip.
const ATTACHMENT_ACCEPT = '.jpg,.jpeg,.png,.webp,.heic,.heif,.mp4,.mov,.webm,.pdf';
const MAX_ATTACHMENTS_PER_BATCH = 5;

const textareaClass =
  'w-full rounded border border-outline bg-surface px-3 py-2 text-data-md text-on-surface focus:outline-none focus:ring-2 focus:ring-primary/40';

const COMPLAINT_STATUS_OPTIONS = [
  { label: 'All statuses', value: '' },
  { label: 'Open', value: 'open' },
  { label: 'Analysis', value: 'analysis' },
  { label: 'Action', value: 'action' },
  { label: 'Verify', value: 'verify' },
  { label: 'Closed', value: 'closed' },
];

const COMPLAINT_CATEGORY_OPTIONS = [
  { label: 'All categories', value: '' },
  { label: 'Chargeable', value: 'chargeable' },
  { label: 'Non-chargeable', value: 'non_chargeable' },
];

const PRIORITY_OPTIONS: { label: string; value: TicketPriority }[] = [
  { label: 'Normal', value: 'normal' },
  { label: 'High', value: 'high' },
  { label: 'Critical', value: 'critical' },
];

const CHANNEL_OPTIONS: { label: string; value: SourceChannel }[] = [
  { label: 'Web', value: 'web' },
  { label: 'Mobile', value: 'mobile' },
  { label: 'WhatsApp', value: 'whatsapp' },
  { label: 'Email', value: 'email' },
  { label: 'Portal', value: 'portal' },
  { label: 'System', value: 'system' },
];

function complaintStatusTone(status: ServiceComplaint['cmsStatus']) {
  if (status === 'closed') return 'success';
  if (status === 'verify') return 'warning';
  if (status === 'action') return 'info';
  return 'neutral';
}

function currency(amount: number) {
  return new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 2 }).format(amount || 0);
}

function apiError(err: unknown, fallback: string) {
  if (err && typeof err === 'object') {
    const maybe = err as { response?: { data?: { error?: string } } };
    if (maybe.response?.data?.error) return maybe.response.data.error;
  }
  return fallback;
}

export function ComplaintsTab() {
  const [filters, setFilters] = useState<ComplaintFilters>({ cmsStatus: '', cmsCategory: '', complaintTypeId: '', batchNumber: '' });
  const [createOpen, setCreateOpen] = useState(false);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const { data: summary } = useComplaintReportSummary();
  const { data: complaintList, isLoading, isError } = useServiceComplaints(filters);
  const complaints = complaintList?.data ?? [];

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-headline-md font-semibold text-on-surface">CMS Complaints</h2>
          <p className="text-body-sm text-on-surface-variant">8D complaint intake, action, verification, closure, and quotation escalation.</p>
        </div>
        <Button onClick={() => setCreateOpen(true)}>
          <Plus className="h-4 w-4" aria-hidden />
          New Complaint
        </Button>
      </div>

      <div className="grid gap-4 md:grid-cols-4">
        <MetricCard title="Open Stages" value={(summary?.statusCounts.open ?? 0) + (summary?.statusCounts.analysis ?? 0) + (summary?.statusCounts.action ?? 0) + (summary?.statusCounts.verify ?? 0)} />
        <MetricCard title="Closed Complaints" value={summary?.tat.closedCount ?? 0} />
        <MetricCard title="Avg TAT (hrs)" value={summary?.tat.averageHours ?? '—'} />
        <MetricCard title="Chargeable Revenue" value={currency(summary?.revenueByCategory.chargeable ?? 0)} />
      </div>

      <div className="flex flex-wrap items-end gap-3">
        <div className="w-44">
          <Select label="Status" name="complaint-status" options={COMPLAINT_STATUS_OPTIONS} value={filters.cmsStatus ?? ''} onChange={(e) => setFilters((current) => ({ ...current, cmsStatus: e.target.value as ComplaintFilters['cmsStatus'] }))} />
        </div>
        <div className="w-44">
          <Select label="Category" name="complaint-category" options={COMPLAINT_CATEGORY_OPTIONS} value={filters.cmsCategory ?? ''} onChange={(e) => setFilters((current) => ({ ...current, cmsCategory: e.target.value as ComplaintFilters['cmsCategory'] }))} />
        </div>
        <div className="w-44">
          <Input label="Batch no." name="complaint-batch-filter" placeholder="Filter by batch…" value={filters.batchNumber ?? ''} onChange={(e) => setFilters((current) => ({ ...current, batchNumber: e.target.value }))} />
        </div>
      </div>

      {isLoading ? (
        <div className="flex h-40 items-center justify-center"><Spinner /></div>
      ) : isError ? (
        <EmptyState title="Failed to load complaints" description="Refresh and try again." />
      ) : complaints.length === 0 ? (
        <EmptyState title="No complaints in this view" description="Raise a complaint or loosen the filters." action={<Button onClick={() => setCreateOpen(true)}>New Complaint</Button>} />
      ) : (
        <div className="overflow-x-auto rounded-lg border border-outline">
          <table className="w-full min-w-[900px] text-left text-body-sm">
            <thead className="bg-surface-variant text-on-surface-variant">
              <tr>
                <th className="px-3 py-2 font-medium">CMS No</th>
                <th className="px-3 py-2 font-medium">Complaint</th>
                <th className="px-3 py-2 font-medium">Type</th>
                <th className="px-3 py-2 font-medium">Category</th>
                <th className="px-3 py-2 font-medium">Stage</th>
                <th className="px-3 py-2 font-medium">Amount</th>
                <th className="px-3 py-2 font-medium">Expected</th>
              </tr>
            </thead>
            <tbody>
              {complaints.map((complaint) => (
                <tr key={complaint.id} className="cursor-pointer border-t border-outline bg-surface hover:bg-surface-variant/60" onClick={() => setSelectedId(complaint.id)}>
                  <td className="px-3 py-2 font-mono text-data-md text-on-surface">{complaint.cmsNo}</td>
                  <td className="px-3 py-2 text-on-surface">{complaint.title}</td>
                  <td className="px-3 py-2 text-on-surface-variant">{typeof complaint.complaintTypeId === 'object' && complaint.complaintTypeId ? complaint.complaintTypeId.name : 'Unspecified'}</td>
                  <td className="px-3 py-2"><Badge tone={complaint.cmsCategory === 'chargeable' ? 'warning' : 'neutral'}>{humanize(complaint.cmsCategory)}</Badge></td>
                  <td className="px-3 py-2"><Badge tone={complaintStatusTone(complaint.cmsStatus)}>{humanize(complaint.cmsStatus)}</Badge></td>
                  <td className="px-3 py-2 text-on-surface">{currency(complaint.amount)}</td>
                  <td className="px-3 py-2 text-on-surface-variant">{formatDateTime(complaint.expectedCompletion)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <CreateComplaintModal open={createOpen} onClose={() => setCreateOpen(false)} />
      <ComplaintDrawer complaintId={selectedId} onClose={() => setSelectedId(null)} />
    </div>
  );
}

function MetricCard({ title, value }: { title: string; value: string | number }) {
  return <Card><CardHeader title={title} /><CardBody className="text-2xl font-semibold text-on-surface">{value}</CardBody></Card>;
}

function CreateComplaintModal({ open, onClose }: { open: boolean; onClose: () => void }) {
  const { createComplaint, uploadComplaintAttachments } = useServiceDeskMutations();
  const { data: complaintTypes = [] } = useMasterItems('complaint_type');
  const { data: customers = [] } = useAccountsCustomers();
  const { data: productData } = useProducts({ page: 1, search: '' });
  const { data: users = [] } = useUserOptions(open);
  const products = productData?.data ?? [];
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [attachedFiles, setAttachedFiles] = useState<File[]>([]);
  const [form, setForm] = useState({
    title: '',
    description: '',
    sourceChannel: 'web' as SourceChannel,
    priority: 'normal' as TicketPriority,
    complaintTypeId: '',
    cmsCategory: 'non_chargeable' as ServiceComplaint['cmsCategory'],
    serialNo: '',
    purchaseDate: '',
    invoiceNo: '',
    productId: '',
    productName: '',
    productQuantity: '1',
    dealerId: '',
    employeeId: '',
    expectedCompletion: '',
    amount: '0',
    instructions: '',
    comments: '',
    batchNumber: '',
    responsiblePersonId: '',
    sampleReceived: false,
    replacementRequired: false,
    qcReportSummary: '',
  });
  const select = <K extends keyof typeof form>(key: K, value: (typeof form)[K]) => setForm((current) => ({ ...current, [key]: value }));

  const submit = () => {
    if (!form.title.trim() || !form.description.trim()) {
      toast.error('Title and description are required.');
      return;
    }
    createComplaint.mutate({
      title: form.title.trim(),
      description: form.description.trim(),
      sourceChannel: form.sourceChannel,
      priority: form.priority,
      complaintTypeId: form.complaintTypeId || undefined,
      cmsCategory: form.cmsCategory,
      serialNo: form.serialNo || undefined,
      purchaseDate: form.purchaseDate || undefined,
      invoiceNo: form.invoiceNo || undefined,
      productId: form.productId || undefined,
      productName: form.productName.trim() || undefined,
      productQuantity: Number(form.productQuantity) || 0,
      dealerId: form.dealerId || undefined,
      employeeId: form.employeeId || undefined,
      expectedCompletion: form.expectedCompletion || undefined,
      amount: Number(form.amount) || 0,
      instructions: form.instructions || undefined,
      feedback: form.comments.trim() || undefined,
      comments: form.comments.trim() || undefined,
      batchNumber: form.batchNumber || undefined,
      responsiblePersonId: form.responsiblePersonId || undefined,
      sampleReceived: form.sampleReceived,
      replacementRequired: form.replacementRequired,
      qcReportSummary: form.qcReportSummary || undefined,
    }, {
      onSuccess: async (complaint) => {
        if (attachedFiles.length > 0) {
          try {
            await uploadComplaintAttachments.mutateAsync({
              id: complaint.id,
              files: attachedFiles,
            });
            toast.success(`Complaint ${complaint.cmsNo} created with ${attachedFiles.length} attachment(s).`);
          } catch {
            toast.success(`Complaint ${complaint.cmsNo} created (attachment upload failed).`);
          }
        } else {
          toast.success(`Complaint ${complaint.cmsNo} created.`);
        }
        setAttachedFiles([]);
        onClose();
      },
      onError: (err) => toast.error(apiError(err, 'Failed to create complaint.')),
    });
  };

  return (
    <Modal open={open} onClose={onClose} title="New Customer Complaint" footer={<div className="flex justify-end gap-2"><Button variant="ghost" onClick={onClose}>Cancel</Button><Button onClick={submit} disabled={createComplaint.isPending || uploadComplaintAttachments.isPending}>{createComplaint.isPending || uploadComplaintAttachments.isPending ? 'Submitting…' : 'Submit Complaint'}</Button></div>}>
      <div className="grid gap-3 md:grid-cols-2">
        <Input label="Title" name="complaint-title" placeholder="e.g. Color variation in white masterbatch" value={form.title} onChange={(e) => select('title', e.target.value)} />
        <Select label="Complaint type" name="complaint-type" value={form.complaintTypeId} onChange={(e) => select('complaintTypeId', e.target.value)} options={[{ label: 'Select complaint type', value: '' }, ...complaintTypes.map((item) => ({ label: item.name, value: item._id }))]} />
        <div className="md:col-span-2">
          <label htmlFor="complaint-description" className="mb-1.5 block text-body-sm font-medium text-on-surface">Description</label>
          <textarea id="complaint-description" rows={3} className={textareaClass} placeholder="Describe the customer issue, defect or rejection in detail..." value={form.description} onChange={(e) => select('description', e.target.value)} />
        </div>
        <Input
          label="Product Name"
          name="complaint-product-name"
          placeholder="e.g. Masterbatch White 101, Black MB, etc."
          value={form.productName}
          onChange={(e) => select('productName', e.target.value)}
        />
        <Select
          label="Catalog Product (optional link)"
          name="complaint-product"
          value={form.productId}
          onChange={(e) => {
            const prodId = e.target.value;
            select('productId', prodId);
            if (prodId) {
              const found = products.find((p) => p._id === prodId);
              if (found && !form.productName) {
                select('productName', found.name);
              }
            }
          }}
          options={[{ label: 'None / Custom product', value: '' }, ...products.map((product) => ({ label: `${product.name}${product.sku ? ` (${product.sku})` : ''}`, value: product._id }))]}
        />
        <div className="md:col-span-2">
          <label htmlFor="complaint-comments" className="mb-1.5 block text-body-sm font-medium text-on-surface">Comments / Client Feedback</label>
          <textarea
            id="complaint-comments"
            rows={3}
            className={textareaClass}
            placeholder="Enter client remarks, comments, customer quotes, or specific feedback..."
            value={form.comments}
            onChange={(e) => select('comments', e.target.value)}
          />
        </div>
        <div className="md:col-span-2 rounded border border-outline/70 p-3 bg-surface-variant/20">
          <label className="mb-1.5 block text-body-sm font-medium text-on-surface">
            Attachments (Photos, Videos, Invoice Copies, QC Evidence)
          </label>
          <div className="flex flex-wrap items-center gap-2">
            <input
              ref={fileInputRef}
              type="file"
              multiple
              accept={ATTACHMENT_ACCEPT}
              onChange={(e) => {
                const files = Array.from(e.target.files || []);
                if (files.length + attachedFiles.length > MAX_ATTACHMENTS_PER_BATCH) {
                  toast.error(`You can attach up to ${MAX_ATTACHMENTS_PER_BATCH} files.`);
                  return;
                }
                setAttachedFiles((prev) => [...prev, ...files]);
              }}
              className="text-body-sm text-on-surface"
            />
          </div>
          {attachedFiles.length > 0 && (
            <div className="mt-2 flex flex-wrap gap-2">
              {attachedFiles.map((file, idx) => (
                <div key={idx} className="flex items-center gap-1.5 rounded bg-surface border border-outline px-2 py-1 text-body-sm text-on-surface">
                  <Paperclip className="h-3.5 w-3.5 text-on-surface-variant" />
                  <span className="max-w-[200px] truncate">{file.name}</span>
                  <button
                    type="button"
                    className="ml-1 text-danger-text hover:opacity-75 font-bold"
                    onClick={() => setAttachedFiles((prev) => prev.filter((_, i) => i !== idx))}
                  >
                    ×
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
        <Select label="Category" name="complaint-category-new" value={form.cmsCategory} onChange={(e) => select('cmsCategory', e.target.value as ServiceComplaint['cmsCategory'])} options={[{ label: 'Chargeable', value: 'chargeable' }, { label: 'Non-chargeable', value: 'non_chargeable' }]} />
        <Select label="Priority" name="complaint-priority" value={form.priority} onChange={(e) => select('priority', e.target.value as TicketPriority)} options={PRIORITY_OPTIONS} />
        <Select label="Channel" name="complaint-channel" value={form.sourceChannel} onChange={(e) => select('sourceChannel', e.target.value as SourceChannel)} options={CHANNEL_OPTIONS} />
        <Input label="Serial no." name="complaint-serial" value={form.serialNo} onChange={(e) => select('serialNo', e.target.value)} />
        <Input label="Batch no." name="complaint-batch" value={form.batchNumber} onChange={(e) => select('batchNumber', e.target.value)} />
        <Input label="Purchase date" name="complaint-purchase-date" type="date" value={form.purchaseDate} onChange={(e) => select('purchaseDate', e.target.value)} />
        <Input label="Invoice no." name="complaint-invoice" value={form.invoiceNo} onChange={(e) => select('invoiceNo', e.target.value)} />
        <Input label="Qty" name="complaint-qty" type="number" min={0} value={form.productQuantity} onChange={(e) => select('productQuantity', e.target.value)} />
        <Select label="Dealer / Client" name="complaint-dealer" value={form.dealerId} onChange={(e) => select('dealerId', e.target.value)} options={[{ label: 'Select dealer / client', value: '' }, ...customers.map((customer: any) => ({ label: customer.name, value: customer._id }))]} />
        <Select label="Employee / BDM" name="complaint-employee" value={form.employeeId} onChange={(e) => select('employeeId', e.target.value)} options={[{ label: 'Select employee', value: '' }, ...users.map((user) => ({ label: user.display_name, value: user.id }))]} />
        <Input label="Expected completion" name="complaint-expected" type="datetime-local" value={form.expectedCompletion} onChange={(e) => select('expectedCompletion', e.target.value)} />
        <Input label="Amount" name="complaint-amount" type="number" min={0} value={form.amount} onChange={(e) => select('amount', e.target.value)} />
        <Select label="Responsible person" name="complaint-responsible" value={form.responsiblePersonId} onChange={(e) => select('responsiblePersonId', e.target.value)} options={[{ label: 'Assign later', value: '' }, ...users.map((user) => ({ label: user.display_name, value: user.id }))]} />
        <div className="flex items-end gap-4 pb-1.5">
          <label className="flex items-center gap-2 text-body-sm text-on-surface">
            <input type="checkbox" className="h-4 w-4 rounded border-outline" checked={form.sampleReceived} onChange={(e) => select('sampleReceived', e.target.checked)} />
            Sample received
          </label>
          <label className="flex items-center gap-2 text-body-sm text-on-surface">
            <input type="checkbox" className="h-4 w-4 rounded border-outline" checked={form.replacementRequired} onChange={(e) => select('replacementRequired', e.target.checked)} />
            Replacement required
          </label>
        </div>
        <div className="md:col-span-2">
          <label htmlFor="complaint-instructions" className="mb-1.5 block text-body-sm font-medium text-on-surface">Instructions / Remarks</label>
          <textarea id="complaint-instructions" rows={2} className={textareaClass} value={form.instructions} onChange={(e) => select('instructions', e.target.value)} />
        </div>
        <div className="md:col-span-2">
          <label htmlFor="complaint-qc-summary" className="mb-1.5 block text-body-sm font-medium text-on-surface">QC report summary (if already known)</label>
          <textarea id="complaint-qc-summary" rows={2} className={textareaClass} value={form.qcReportSummary} onChange={(e) => select('qcReportSummary', e.target.value)} />
        </div>
      </div>
    </Modal>
  );
}

function ComplaintDrawer({ complaintId, onClose }: { complaintId: string | null; onClose: () => void }) {
  const { data: complaint, isLoading } = useServiceComplaint(complaintId);
  const {
    updateComplaint,
    transitionComplaint,
    transitionComplaintStage,
    uploadComplaintAttachments,
    deleteComplaintAttachment,
    raiseComplaintCreditNote,
  } = useServiceDeskMutations();
  const user = useUser();
  const isAdmin = ['admin', 'superadmin', 'dept_admin', 'director', 'management'].includes(user?.role?.toLowerCase() || '') ||
                  ['admin', 'management', 'service_desk'].includes(user?.department?.toLowerCase() || '');
  const { data: users = [] } = useUserOptions(!!complaintId);
  const [draft, setDraft] = useState({ containmentAction: '', rootCause: '', correctiveAction: '', preventiveAction: '', feedback: '', completionDate: '', investigation: '', action: '', supervision: '' });
  const [caseDraft, setCaseDraft] = useState({ responsiblePersonId: '', sampleReceived: false, replacementRequired: false, qcSampleId: '', qcReportSummary: '' });
  const [adminStatus, setAdminStatus] = useState<ServiceComplaint['cmsStatus']>('open');
  const [adminStage, setAdminStage] = useState<ComplaintStage>('registered');
  const [solutionReply, setSolutionReply] = useState('');
  const [creditNoteOpen, setCreditNoteOpen] = useState(false);

  const { data: eligibleInvoices = [] } = useComplaintEligibleInvoices(!!complaintId);
  const linkedInvoice = eligibleInvoices.find((invoice) => invoice._id === complaint?.invoiceId);

  useEffect(() => {
    if (!complaint) return;
    setDraft({
      containmentAction: complaint.containmentAction || '',
      rootCause: complaint.rootCause || '',
      correctiveAction: complaint.correctiveAction || '',
      preventiveAction: complaint.preventiveAction || '',
      feedback: complaint.feedback || '',
      completionDate: complaint.completionDate ? String(complaint.completionDate).slice(0, 16) : '',
      investigation: complaint.eightD?.investigation?.observation || '',
      action: complaint.eightD?.action?.action || '',
      supervision: complaint.eightD?.supervision?.comments || '',
    });
    setCaseDraft({
      responsiblePersonId: typeof complaint.responsiblePersonId === 'object' && complaint.responsiblePersonId ? (complaint.responsiblePersonId.id || complaint.responsiblePersonId._id || '') : (complaint.responsiblePersonId as string) || '',
      sampleReceived: Boolean(complaint.sampleReceived),
      replacementRequired: Boolean(complaint.replacementRequired),
      qcSampleId: complaint.qcSampleId || '',
      qcReportSummary: complaint.qcReportSummary || '',
    });
    setAdminStatus(complaint.cmsStatus || 'open');
    setAdminStage(complaint.cmsStage || 'registered');
    setSolutionReply(complaint.closureSummary || '');
  }, [complaint]);

  const save = () => {
    if (!complaint) return;
    updateComplaint.mutate({
      id: complaint.id,
      containmentAction: draft.containmentAction,
      rootCause: draft.rootCause,
      correctiveAction: draft.correctiveAction,
      preventiveAction: draft.preventiveAction,
      feedback: draft.feedback,
      completionDate: draft.completionDate || undefined,
      eightD: {
        investigation: { observation: draft.investigation },
        action: { action: draft.action },
        supervision: { comments: draft.supervision },
      },
    }, { onSuccess: () => toast.success('Complaint details saved.'), onError: (err) => toast.error(apiError(err, 'Could not save complaint changes.')) });
  };

  const saveCase = () => {
    if (!complaint) return;
    updateComplaint.mutate({
      id: complaint.id,
      responsiblePersonId: caseDraft.responsiblePersonId || null,
      sampleReceived: caseDraft.sampleReceived,
      replacementRequired: caseDraft.replacementRequired,
      qcSampleId: caseDraft.qcSampleId || null,
      qcReportSummary: caseDraft.qcReportSummary,
    }, { onSuccess: () => toast.success('Case details saved.'), onError: (err) => toast.error(apiError(err, 'Could not save case details.')) });
  };

  const submitAdminSolution = async () => {
    if (!complaint) return;
    try {
      const updatePayload: Record<string, unknown> = {};
      if (solutionReply.trim()) {
        updatePayload.closureSummary = solutionReply.trim();
        updatePayload.solutionReply = solutionReply.trim();
        updatePayload.correctiveAction = complaint.correctiveAction
          ? `${complaint.correctiveAction}\n\n[Admin Solution - ${new Date().toLocaleDateString()}]: ${solutionReply.trim()}`
          : solutionReply.trim();
      }
      if (adminStage && adminStage !== complaint.cmsStage) {
        await transitionComplaintStage.mutateAsync({
          id: complaint.id,
          toStage: adminStage,
          note: solutionReply.trim() || 'Admin stage updated',
        });
      }
      if (adminStatus && adminStatus !== complaint.cmsStatus) {
        await transitionComplaint.mutateAsync({
          id: complaint.id,
          toStatus: adminStatus,
          note: solutionReply.trim() || 'Admin status updated',
        });
      }
      if (Object.keys(updatePayload).length > 0) {
        await updateComplaint.mutateAsync({
          id: complaint.id,
          ...updatePayload,
        });
      }
      toast.success('Admin solution and status updated.');
    } catch (err) {
      toast.error(apiError(err, 'Failed to update solution.'));
    }
  };

  const linkInvoice = (invoiceId: string) => {
    if (!complaint) return;
    updateComplaint.mutate({ id: complaint.id, invoiceId: invoiceId || null }, {
      onSuccess: () => toast.success(invoiceId ? 'Invoice linked.' : 'Invoice unlinked.'),
      onError: (err) => toast.error(apiError(err, 'Could not link invoice.')),
    });
  };

  return (
    <Drawer open={!!complaintId} onClose={onClose} width="w-[680px]" title={complaint ? `${complaint.cmsNo} — ${complaint.title}` : 'Complaint'}>
      {isLoading || !complaint ? <div className="flex h-40 items-center justify-center"><Spinner /></div> : (
        <div className="flex flex-col gap-4 p-4">
          <div className="flex flex-wrap gap-2">
            <Badge tone={complaintStatusTone(complaint.cmsStatus)}>{humanize(complaint.cmsStatus)}</Badge>
            <Badge tone="info">{STAGE_LABELS[complaint.cmsStage || 'registered']}</Badge>
            <Badge tone={complaint.cmsCategory === 'chargeable' ? 'warning' : 'neutral'}>{humanize(complaint.cmsCategory)}</Badge>
            <Badge tone={priorityTone(complaint.priority)}>{humanize(complaint.priority)}</Badge>
          </div>

          <Card>
            <CardHeader title="Complaint Details" subtitle={typeof complaint.complaintTypeId === 'object' && complaint.complaintTypeId ? complaint.complaintTypeId.name : 'Unspecified type'} />
            <CardBody className="grid grid-cols-2 gap-x-4 gap-y-2 text-body-sm">
              <span className="text-on-surface-variant">Dealer / Client</span><span className="text-on-surface">{typeof complaint.dealerId === 'object' && complaint.dealerId ? complaint.dealerId.name || '—' : '—'}</span>
              <span className="text-on-surface-variant">Employee / BDM</span><span className="text-on-surface">{personName(complaint.employeeId, '—')}</span>
              <span className="text-on-surface-variant">Product Name</span><span className="text-on-surface font-medium">{complaint.productSnapshot?.name || (typeof complaint.productId === 'object' && complaint.productId ? complaint.productId.name || '—' : '—')}</span>
              <span className="text-on-surface-variant">Batch no.</span><span className="text-on-surface">{complaint.batchNumber || '—'}</span>
              <span className="text-on-surface-variant">Invoice no.</span><span className="text-on-surface">{complaint.invoiceNo || '—'}</span>
              <span className="text-on-surface-variant">Amount</span><span className="text-on-surface">{currency(complaint.amount)}</span>
              <span className="text-on-surface-variant">Client Comments</span><span className="text-on-surface whitespace-pre-wrap">{complaint.feedback || '—'}</span>
              <span className="text-on-surface-variant">Expected completion</span><span className="text-on-surface">{formatDateTime(complaint.expectedCompletion)}</span>
              <span className="text-on-surface-variant">Created</span><span className="text-on-surface">{formatDateTime(complaint.createdAt)}</span>
            </CardBody>
          </Card>

          <Card className="border-2 border-primary/30 bg-primary/[0.02]">
            <CardHeader
              title={isAdmin ? "Admin Review & Solution Reply" : "Review & Solution Reply"}
              subtitle={isAdmin ? "Admin actions: review complaint, submit solution reply, and update the status." : "Review complaint, reply with technical solution or corrective action, and update the status."}
            />
            <CardBody className="grid gap-3">
              <div className="grid gap-3 md:grid-cols-2">
                <div>
                  <label className="mb-1.5 block text-body-sm font-medium text-on-surface">Update Status (5-State)</label>
                  <Select
                    name="admin-status"
                    value={adminStatus}
                    onChange={(e) => setAdminStatus(e.target.value as ServiceComplaint['cmsStatus'])}
                    options={[
                      { label: 'Open', value: 'open' },
                      { label: 'Analysis', value: 'analysis' },
                      { label: 'Action', value: 'action' },
                      { label: 'Verify', value: 'verify' },
                      { label: 'Closed', value: 'closed' },
                    ]}
                  />
                </div>
                <div>
                  <label className="mb-1.5 block text-body-sm font-medium text-on-surface">Update Working Stage (8D)</label>
                  <Select
                    name="admin-stage"
                    value={adminStage}
                    onChange={(e) => setAdminStage(e.target.value as ComplaintStage)}
                    options={Object.entries(STAGE_LABELS).map(([val, lbl]) => ({ label: lbl, value: val }))}
                  />
                </div>
              </div>
              <div>
                <label htmlFor="admin-solution-reply" className="mb-1.5 block text-body-sm font-medium text-on-surface">
                  Admin Solution / Resolution Reply
                </label>
                <textarea
                  id="admin-solution-reply"
                  rows={3}
                  className={textareaClass}
                  placeholder="Enter technical solution, root-cause resolution, or reply instructions to client/BDM..."
                  value={solutionReply}
                  onChange={(e) => setSolutionReply(e.target.value)}
                />
              </div>
              {complaint.closureSummary && (
                <div className="rounded border border-primary/20 bg-surface p-2.5 text-body-sm">
                  <span className="font-medium text-primary">Current Active Solution / Closure Summary:</span>
                  <p className="mt-1 whitespace-pre-wrap text-on-surface">{complaint.closureSummary}</p>
                </div>
              )}
              <div className="flex justify-end gap-2">
                <Button
                  onClick={submitAdminSolution}
                  disabled={updateComplaint.isPending || transitionComplaint.isPending || transitionComplaintStage.isPending}
                >
                  {(updateComplaint.isPending || transitionComplaint.isPending || transitionComplaintStage.isPending) ? 'Updating…' : 'Submit Solution & Update Status'}
                </Button>
              </div>
            </CardBody>
          </Card>

          <Card>
            <CardHeader title="Workflow" subtitle="5-state 8D lifecycle" action={<DocumentActions docType="service_ticket" docId={complaint.id} />} />
            <CardBody className="flex flex-wrap gap-2">
              {complaint.availableCmsTransitions?.length ? complaint.availableCmsTransitions.map((transition) => (
                <Button key={transition.to} size="sm" variant="secondary" disabled={transitionComplaint.isPending} onClick={() => transitionComplaint.mutate({ id: complaint.id, toStatus: transition.to }, { onSuccess: () => toast.success(`${transition.label} complete.`), onError: (err) => toast.error(apiError(err, 'Transition failed.')) })}>{transition.label}</Button>
              )) : <span className="text-body-sm text-on-surface-variant">No status move available from here.</span>}
            </CardBody>
          </Card>

          <Card>
            <CardHeader title="Stage" subtitle="8-step working stage — client's day-to-day tracker" />
            <CardBody className="flex flex-wrap gap-2">
              {complaint.availableCmsStageTransitions?.length ? complaint.availableCmsStageTransitions.map((transition) => (
                <Button
                  key={transition.to}
                  size="sm"
                  variant="secondary"
                  disabled={transitionComplaintStage.isPending}
                  onClick={() => transitionComplaintStage.mutate({ id: complaint.id, toStage: transition.to }, {
                    onSuccess: () => toast.success(`${transition.label} complete.`),
                    onError: (err) => toast.error(apiError(err, 'Stage move failed.')),
                  })}
                >
                  {transition.label}
                </Button>
              )) : <span className="text-body-sm text-on-surface-variant">No stage move available from here.</span>}
            </CardBody>
          </Card>

          <Card>
            <CardHeader title="8D Investigation" subtitle="Stamped updates are written with the current user and time." />
            <CardBody className="grid gap-3">
              <FieldArea id="complaint-investigation" label="Investigation observation" value={draft.investigation} onChange={(value) => setDraft((current) => ({ ...current, investigation: value }))} />
              <FieldArea id="complaint-containment" label="Containment action" value={draft.containmentAction} onChange={(value) => setDraft((current) => ({ ...current, containmentAction: value }))} />
              <FieldArea id="complaint-root-cause" label="Root cause" value={draft.rootCause} onChange={(value) => setDraft((current) => ({ ...current, rootCause: value }))} />
              <FieldArea id="complaint-action" label="Action taken" value={draft.action} onChange={(value) => setDraft((current) => ({ ...current, action: value }))} />
              <FieldArea id="complaint-corrective" label="Corrective action" value={draft.correctiveAction} onChange={(value) => setDraft((current) => ({ ...current, correctiveAction: value }))} />
              <FieldArea id="complaint-preventive" label="Preventive action" value={draft.preventiveAction} onChange={(value) => setDraft((current) => ({ ...current, preventiveAction: value }))} />
              <FieldArea id="complaint-supervision" label="Supervision comments" value={draft.supervision} onChange={(value) => setDraft((current) => ({ ...current, supervision: value }))} />
              <div className="grid gap-3 md:grid-cols-2">
                <Input label="Completion date" name="complaint-completion-date" type="datetime-local" value={draft.completionDate} onChange={(e) => setDraft((current) => ({ ...current, completionDate: e.target.value }))} />
                <FieldArea id="complaint-feedback" label="Feedback" value={draft.feedback} onChange={(value) => setDraft((current) => ({ ...current, feedback: value }))} rows={3} />
              </div>
              <div className="flex justify-end"><Button onClick={save} disabled={updateComplaint.isPending}>{updateComplaint.isPending ? 'Saving…' : 'Save Complaint Details'}</Button></div>
            </CardBody>
          </Card>

          <Card>
            <CardHeader title="Case Management" subtitle="Gates the stage buttons above — acknowledging needs a responsible person, investigation needs a received sample." />
            <CardBody className="grid gap-3">
              <Select
                label="Responsible person"
                name="case-responsible"
                value={caseDraft.responsiblePersonId}
                onChange={(e) => setCaseDraft((current) => ({ ...current, responsiblePersonId: e.target.value }))}
                options={[{ label: 'Unassigned', value: '' }, ...users.map((user) => ({ label: user.display_name, value: user.id }))]}
              />
              <div className="flex flex-wrap gap-4">
                <label className="flex items-center gap-2 text-body-sm text-on-surface">
                  <input type="checkbox" className="h-4 w-4 rounded border-outline" checked={caseDraft.sampleReceived} onChange={(e) => setCaseDraft((current) => ({ ...current, sampleReceived: e.target.checked }))} />
                  Sample received
                </label>
                <label className="flex items-center gap-2 text-body-sm text-on-surface">
                  <input type="checkbox" className="h-4 w-4 rounded border-outline" checked={caseDraft.replacementRequired} onChange={(e) => setCaseDraft((current) => ({ ...current, replacementRequired: e.target.checked }))} />
                  Replacement required
                </label>
              </div>
              <Input label="QC sample ID (optional link to a Quality sample record)" name="case-qc-sample" value={caseDraft.qcSampleId} onChange={(e) => setCaseDraft((current) => ({ ...current, qcSampleId: e.target.value }))} />
              <FieldArea id="case-qc-summary" label="QC report summary" value={caseDraft.qcReportSummary} onChange={(value) => setCaseDraft((current) => ({ ...current, qcReportSummary: value }))} rows={3} />
              <div className="flex justify-end"><Button onClick={saveCase} disabled={updateComplaint.isPending}>{updateComplaint.isPending ? 'Saving…' : 'Save Case Details'}</Button></div>
            </CardBody>
          </Card>

          <AttachmentsCard
            complaint={complaint}
            onUpload={(files, caption) => uploadComplaintAttachments.mutate({ id: complaint.id, files, captions: files.map(() => caption) }, {
              onSuccess: () => toast.success('Attachment(s) uploaded.'),
              onError: (err) => toast.error(apiError(err, 'Upload failed.')),
            })}
            onDelete={(attachmentId) => deleteComplaintAttachment.mutate({ id: complaint.id, attachmentId }, {
              onSuccess: () => toast.success('Attachment removed.'),
              onError: (err) => toast.error(apiError(err, 'Could not remove attachment.')),
            })}
            uploading={uploadComplaintAttachments.isPending}
          />

          <Card>
            <CardHeader title="Invoice & Credit Note" subtitle="A credit note must reference a posted invoice — search by invoice no. or customer name." />
            <CardBody className="grid gap-3">
              <SearchableSelect
                label="Linked invoice"
                name="case-invoice"
                value={complaint.invoiceId || ''}
                disabled={updateComplaint.isPending}
                onChange={(value) => linkInvoice(value)}
                clearable
                placeholder="No invoice linked"
                searchPlaceholder="Search invoice no. or customer…"
                emptyText="No creditable invoices found."
                options={[
                  ...eligibleInvoices.map((invoice) => ({
                    value: invoice._id,
                    label: invoice.invoiceNo,
                    description: `${invoice.customerName || 'No customer name'} · ${currency(invoice.grandTotalMinor / 100)}`,
                  })),
                  ...(complaint.invoiceId && !linkedInvoice ? [{ value: complaint.invoiceId, label: `Linked invoice (${complaint.invoiceId})` }] : []),
                ]}
              />
              {complaint.creditNoteId ? (
                <Badge tone="success">Credit note already raised</Badge>
              ) : (
                <div className="flex items-center justify-between gap-3">
                  <p className="text-body-sm text-on-surface-variant">
                    {complaint.invoiceId ? 'Ready to raise a credit note against the linked invoice.' : 'Link an invoice above before raising a credit note.'}
                  </p>
                  <Button size="sm" variant="secondary" disabled={!complaint.invoiceId || complaint.cmsStatus === 'closed'} onClick={() => setCreditNoteOpen(true)}>Raise Credit Note</Button>
                </div>
              )}
            </CardBody>
          </Card>

          <Card>
            <CardHeader title="Followups" subtitle="Complaint followups use the shared activity log introduced in Phase 0.1." />
            <CardBody><FollowupPanel parentType="service_ticket" parentId={complaint.id} /></CardBody>
          </Card>

          <Card>
            <CardHeader title="Closure Snapshot" />
            <CardBody className="grid grid-cols-2 gap-x-4 gap-y-2 text-body-sm">
              <span className="text-on-surface-variant">Completion</span><span className="text-on-surface">{formatDateTime(complaint.completionDate)}</span>
              <span className="text-on-surface-variant">Feedback</span><span className="text-on-surface whitespace-pre-wrap">{complaint.feedback || '—'}</span>
            </CardBody>
          </Card>
        </div>
      )}
      {complaint ? (
        <CreditNoteModal
          open={creditNoteOpen}
          complaint={complaint}
          onClose={() => setCreditNoteOpen(false)}
          onSubmit={(payload) => raiseComplaintCreditNote.mutate({ id: complaint.id, ...payload }, {
            onSuccess: (result) => { toast.success(`Credit note ${result.creditNote.creditNoteNo} raised.`); setCreditNoteOpen(false); },
            onError: (err) => toast.error(apiError(err, 'Could not raise credit note.')),
          })}
          submitting={raiseComplaintCreditNote.isPending}
        />
      ) : null}
    </Drawer>
  );
}

function AttachmentsCard({ complaint, onUpload, onDelete, uploading }: {
  complaint: ServiceComplaint;
  onUpload: (files: File[], caption: string) => void;
  onDelete: (attachmentId: string) => void;
  uploading: boolean;
}) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [pendingFiles, setPendingFiles] = useState<File[]>([]);
  const [caption, setCaption] = useState('');
  const attachments = complaint.attachments || [];

  const pickFiles = (fileList: FileList | null) => {
    const files = Array.from(fileList || []);
    if (files.length > MAX_ATTACHMENTS_PER_BATCH) {
      toast.error(`Upload at most ${MAX_ATTACHMENTS_PER_BATCH} files at a time.`);
      return;
    }
    setPendingFiles(files);
  };

  const submit = () => {
    if (!pendingFiles.length) return;
    onUpload(pendingFiles, caption);
    setPendingFiles([]);
    setCaption('');
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  return (
    <Card>
      <CardHeader title="Attachments" subtitle="Photos, videos, or a QC report — evidence for the complaint." />
      <CardBody className="grid gap-3">
        {attachments.length === 0 ? (
          <p className="text-body-sm text-on-surface-variant">No attachments yet.</p>
        ) : (
          <div className="grid grid-cols-2 gap-3 md:grid-cols-3">
            {attachments.map((attachment) => (
              <div key={attachment._id} className="relative overflow-hidden rounded border border-outline bg-surface-variant/40">
                {attachment.kind === 'photo' ? (
                  <img src={complaintAttachmentUrl(complaint.id, attachment._id)} alt={attachment.caption || attachment.originalName || 'Attachment'} className="h-28 w-full object-cover" />
                ) : (
                  <a href={complaintAttachmentUrl(complaint.id, attachment._id)} target="_blank" rel="noreferrer" className="flex h-28 w-full flex-col items-center justify-center gap-1 text-on-surface-variant hover:text-on-surface">
                    <Paperclip className="h-6 w-6" aria-hidden />
                    <span className="px-2 text-center text-body-sm">{humanize(attachment.kind)}</span>
                  </a>
                )}
                <div className="flex items-start justify-between gap-1 p-1.5">
                  <div className="min-w-0">
                    <p className="truncate text-body-sm text-on-surface" title={attachment.originalName}>{attachment.caption || attachment.originalName || 'Untitled'}</p>
                    <p className="truncate text-body-sm text-on-surface-variant">{attachment.uploadedByName || '—'} · {formatDateTime(attachment.uploadedAt)}</p>
                  </div>
                  <button type="button" aria-label="Remove attachment" className="shrink-0 rounded p-1 text-on-surface-variant hover:bg-surface hover:text-danger-text" onClick={() => onDelete(attachment._id)}>
                    <Trash2 className="h-3.5 w-3.5" aria-hidden />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
        <div className="flex flex-wrap items-end gap-2">
          <input ref={fileInputRef} type="file" multiple accept={ATTACHMENT_ACCEPT} onChange={(e) => pickFiles(e.target.files)} className="text-body-sm text-on-surface" />
          <Input label="Caption (optional)" name="attachment-caption" value={caption} onChange={(e) => setCaption(e.target.value)} />
          <Button size="sm" onClick={submit} disabled={!pendingFiles.length || uploading}>{uploading ? 'Uploading…' : `Upload ${pendingFiles.length || ''}`.trim()}</Button>
        </div>
      </CardBody>
    </Card>
  );
}

function CreditNoteModal({ open, complaint, onClose, onSubmit, submitting }: {
  open: boolean;
  complaint: ServiceComplaint;
  onClose: () => void;
  onSubmit: (payload: { quantity: number; unitPriceMinor?: number; settlementType: 'reduce_ar' | 'refund_cash' | 'refund_bank' | 'store_credit'; note?: string }) => void;
  submitting: boolean;
}) {
  const [form, setForm] = useState({ quantity: String(complaint.productQuantity || 1), unitPrice: '', settlementType: 'reduce_ar' as 'reduce_ar' | 'refund_cash' | 'refund_bank' | 'store_credit', note: '' });

  useEffect(() => {
    if (open) setForm({ quantity: String(complaint.productQuantity || 1), unitPrice: '', settlementType: 'reduce_ar', note: '' });
  }, [open, complaint.productQuantity]);

  const submit = () => {
    onSubmit({
      quantity: Number(form.quantity) || 1,
      unitPriceMinor: form.unitPrice ? Math.round(Number(form.unitPrice) * 100) : undefined,
      settlementType: form.settlementType,
      note: form.note || undefined,
    });
  };

  return (
    <Modal open={open} onClose={onClose} title="Raise Credit Note" footer={<div className="flex justify-end gap-2"><Button variant="ghost" onClick={onClose}>Cancel</Button><Button onClick={submit} disabled={submitting}>{submitting ? 'Raising…' : 'Raise Credit Note'}</Button></div>}>
      <div className="grid gap-3">
        <p className="text-body-sm text-on-surface-variant">Leave unit price blank to use the linked invoice's own line price where the product matches.</p>
        <div className="grid gap-3 md:grid-cols-2">
          <Input label="Quantity" name="cn-quantity" type="number" min={1} value={form.quantity} onChange={(e) => setForm((current) => ({ ...current, quantity: e.target.value }))} />
          <Input label="Unit price (₹, optional)" name="cn-unit-price" type="number" min={0} value={form.unitPrice} onChange={(e) => setForm((current) => ({ ...current, unitPrice: e.target.value }))} />
        </div>
        <Select
          label="Settlement type"
          name="cn-settlement"
          value={form.settlementType}
          onChange={(e) => setForm((current) => ({ ...current, settlementType: e.target.value as typeof current.settlementType }))}
          options={[
            { label: 'Reduce accounts receivable', value: 'reduce_ar' },
            { label: 'Refund — cash', value: 'refund_cash' },
            { label: 'Refund — bank', value: 'refund_bank' },
            { label: 'Store credit', value: 'store_credit' },
          ]}
        />
        <FieldArea id="cn-note" label="Note (optional)" value={form.note} onChange={(value) => setForm((current) => ({ ...current, note: value }))} rows={2} />
      </div>
    </Modal>
  );
}

function FieldArea({ id, label, value, onChange, rows = 3 }: { id: string; label: string; value: string; onChange: (value: string) => void; rows?: number }) {
  return (
    <div>
      <label htmlFor={id} className="mb-1.5 block text-body-sm font-medium text-on-surface">{label}</label>
      <textarea id={id} rows={rows} className={textareaClass} value={value} onChange={(e) => onChange(e.target.value)} />
    </div>
  );
}
